import { FromPriceConfig } from "@/hooks/useFromPrice";
import { TProxyTabType } from "@/components/newcustomize/utils/makeProxyTabType";
import makeDefaultVerifiedProxyCount from "@/components/newcustomize/utils/makeDefaultVerifiedProxyCount";

type PlanTierParams = {
  proxyReplacements: number;
  proxyCount: number;
  customProxyCount: number;
  bandwidth: number;
};

export type PlanTier = {
  title: string;
  /** Drives the "/Proxy" or "/GB" suffix and the per-unit price divisor. */
  unit: "Proxy" | "GB";
  /** What the price is divided by to get the per-unit price. */
  unitCount: number;
  params: PlanTierParams;
  isDefault?: boolean;
  /**
   * Config used to resolve the price from the pricing API. Omitted for tiers
   * the pricing endpoint can't serve (Verified is custom-quoted), which use
   * `staticPrice` instead.
   */
  pricingConfig?: FromPriceConfig;
  staticPrice?: number;
};

export type PlanProduct = {
  title: string;
  icon: string;
  description: string;
  benefits: string[];
  tiers: PlanTier[];
};

// NEXT_PUBLIC_ env vars are inlined at build time, so this resolves to a
// constant, safe to evaluate at module scope.
const VERIFIED_PROXY_COUNT = makeDefaultVerifiedProxyCount();

/**
 * The Plans & Pricing page catalog: per-product metadata plus the config each
 * tier needs to (a) resolve its price from the pricing API and (b) deep-link
 * into customize. Prices themselves are resolved at runtime (see `usePlanData`)
 * so they always match the backend, including pricing experiments.
 */
export const PRICING_PLAN_CATALOG: Record<TProxyTabType, PlanProduct> = {
  datacenter: {
    title: "Proxy Server",
    icon: "/datacenter-icon.svg",
    description: "Fast & Affordable datacenter proxies",
    benefits: ["Fast and affordable"],
    tiers: [
      {
        title: `${(100).toLocaleString()} shared proxies`,
        unit: "Proxy",
        unitCount: 100,
        params: {
          proxyReplacements: 10,
          proxyCount: 100,
          customProxyCount: 1000,
          bandwidth: 250,
        },
        pricingConfig: {
          proxyType: "shared",
          proxySubtype: "default",
          proxyCount: 100,
          bandwidth: 250,
        },
      },
      {
        title: `${(1000).toLocaleString()} shared proxies`,
        unit: "Proxy",
        unitCount: 1000,
        params: {
          proxyReplacements: 10,
          proxyCount: 1000,
          customProxyCount: 1000,
          bandwidth: 250,
        },
        isDefault: true,
        pricingConfig: {
          proxyType: "shared",
          proxySubtype: "default",
          proxyCount: 1000,
          bandwidth: 250,
        },
      },
      {
        title: `${(10000).toLocaleString()} shared proxies`,
        unit: "Proxy",
        unitCount: 10000,
        params: {
          proxyReplacements: 10,
          proxyCount: 10000,
          customProxyCount: 1000,
          bandwidth: 250,
        },
        pricingConfig: {
          proxyType: "shared",
          proxySubtype: "default",
          proxyCount: 10000,
          bandwidth: 250,
        },
      },
    ],
  },
  staticResidential: {
    title: "Static Residential",
    icon: "/static-residential-icon.svg",
    description:
      "High-speed and high-reliability alternative to standard residential proxies",
    benefits: [
      "Recommended for social media / gaming",
      "Up to Unlimited Bandwidth",
      "Dedicated proxy option",
    ],
    tiers: [
      {
        title: `${(20).toLocaleString()} shared proxies`,
        unit: "Proxy",
        unitCount: 20,
        params: {
          proxyReplacements: 10,
          proxyCount: 20,
          customProxyCount: 100,
          bandwidth: 250,
        },
        pricingConfig: {
          proxyType: "shared",
          proxySubtype: "isp",
          proxyCount: 20,
          bandwidth: 250,
        },
      },
      {
        title: `${(1000).toLocaleString()} shared proxies`,
        unit: "Proxy",
        unitCount: 1000,
        params: {
          proxyReplacements: 10,
          proxyCount: 1000,
          customProxyCount: 100,
          bandwidth: 250,
        },
        isDefault: true,
        pricingConfig: {
          proxyType: "shared",
          proxySubtype: "isp",
          proxyCount: 1000,
          bandwidth: 250,
        },
      },
      {
        title: `${(10000).toLocaleString()} shared proxies`,
        unit: "Proxy",
        unitCount: 10000,
        params: {
          proxyReplacements: 10,
          proxyCount: 10000,
          customProxyCount: 100,
          bandwidth: 250,
        },
        pricingConfig: {
          proxyType: "shared",
          proxySubtype: "isp",
          proxyCount: 10000,
          bandwidth: 250,
        },
      },
    ],
  },
  residential: {
    title: "Rotating Residential",
    icon: "/residential-icon.svg",
    description: "80M+ ethically sourced IPs from real residential devices",
    benefits: [
      "80M+ Residential IPs",
      "195 countries",
      "City, State, ZIP & ASN Targeting",
    ],
    tiers: [
      {
        title: `${(1).toLocaleString()} GB`,
        unit: "GB",
        unitCount: 1,
        params: {
          proxyReplacements: 0,
          proxyCount: 80000000,
          customProxyCount: 80000000,
          bandwidth: 1,
        },
        isDefault: true,
        pricingConfig: {
          proxyType: "shared",
          proxySubtype: "residential",
          proxyCount: 80000000,
          bandwidth: 1,
        },
      },
      {
        title: `${(100).toLocaleString()} GB`,
        unit: "GB",
        unitCount: 100,
        params: {
          proxyReplacements: 0,
          proxyCount: 80000000,
          customProxyCount: 80000000,
          bandwidth: 100,
        },
        pricingConfig: {
          proxyType: "shared",
          proxySubtype: "residential",
          proxyCount: 80000000,
          bandwidth: 100,
        },
      },
      {
        title: `${(3000).toLocaleString()} GB`,
        unit: "GB",
        unitCount: 3000,
        params: {
          proxyReplacements: 0,
          proxyCount: 80000000,
          customProxyCount: 80000000,
          bandwidth: 3000,
        },
        pricingConfig: {
          proxyType: "shared",
          proxySubtype: "residential",
          proxyCount: 80000000,
          bandwidth: 3000,
        },
      },
    ],
  },
  verified: {
    title: "Verified",
    icon: "/verified-icon.svg",
    description: "Scrape selected websites with >99% success rate",
    benefits: [
      "5,000 Verified Proxies included",
      "99.9% Success Rate",
      "Unlimited Auto Replacements ",
    ],
    // Verified plans are custom-quoted; the pricing endpoint does not serve
    // them, so these tiers use static list prices.
    tiers: [
      {
        title: `${(250).toLocaleString()} GB`,
        unit: "GB",
        unitCount: 250,
        staticPrice: 150,
        params: {
          proxyReplacements: 0,
          proxyCount: VERIFIED_PROXY_COUNT,
          customProxyCount: VERIFIED_PROXY_COUNT,
          bandwidth: 250,
        },
        isDefault: true,
      },
      {
        title: `${(1000).toLocaleString()} GB`,
        unit: "GB",
        unitCount: 1000,
        staticPrice: 500,
        params: {
          proxyReplacements: 0,
          proxyCount: VERIFIED_PROXY_COUNT,
          customProxyCount: VERIFIED_PROXY_COUNT,
          bandwidth: 1000,
        },
      },
      {
        title: `${(5000).toLocaleString()} GB`,
        unit: "GB",
        unitCount: 5000,
        staticPrice: 1000,
        params: {
          proxyReplacements: 0,
          proxyCount: VERIFIED_PROXY_COUNT,
          customProxyCount: VERIFIED_PROXY_COUNT,
          bandwidth: 5000,
        },
      },
    ],
  },
};
